import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { diacriticSort } from '~/core/helpers/sort/diacritic-sort.helper';
import { Activity } from '~/core/model/activity.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { SportsService } from '~/core/services/sports/sports.service';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';

@Component({
  selector: 'app-organizer-activity-edition-sport',
  templateUrl: './sport.component.html',
})
export class OrganizerActivityEditionSportComponent
  implements OnInit, OnDestroy
{
  private readonly subscriptions = new Subscription();

  public activity: Activity;

  public autocompleteSports = [];
  public isFormValid: boolean;
  public isOverlayDisplayed = false;
  public overlayStatus = SuccessStatusEnum.LOADING;
  public sport: FormControl;

  constructor(
    private readonly sportsService: SportsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly organizerSharedActivityService: OrganizerSharedActivityService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.sport = new FormControl('', Validators.required);

    this.subscriptions.add(
      this.sport.valueChanges.subscribe(() => {
        this.isFormValid = this.sport.valid && this.sport.dirty;
      })
    );

    this.subscriptions.add(
      this.activatedRoute.data
        .pipe(
          mergeMap(({ activityData }) => {
            return this.organizerSharedActivityService.getActivity(
              activityData.activity?.sku
            );
          })
        )
        .subscribe((activity) => {
          const sport = this.sportsService.getSport(activity?.sports?.[0]);

          this.sport.setValue({ text: sport?.name, value: sport?.id });
          this.activity = activity;
        })
    );

    this.autocompleteSports = this.sportsService.sports
      .map((sport) => ({
        text: sport.name,
        value: sport.id,
      }))
      .sort(({ text: currentValue }, { text: nextValue }) =>
        diacriticSort(currentValue, nextValue)
      );
  }

  public get hasInitialValue(): boolean {
    return Boolean(this.activity?.sports?.length);
  }

  public updateSport(): void {
    const sports = [Number(this.sport.value?.value)];

    this.isOverlayDisplayed = true;

    this.organizerSharedActivityService
      .patchActivityById(this.activity.sku, {
        sports,
      })
      .pipe(
        mergeMap(() => {
          return this.organizerSharedActivityService
            .getActivity(this.activity?.sku.toString())
            .pipe(
              repeatUntil(
                (activity) =>
                  JSON.stringify(activity.sports) === JSON.stringify(sports)
              )
            );
        })
      )
      .subscribe(() => {
        this.overlayStatus = SuccessStatusEnum.SUCCESS;
      });
  }
}
